import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiEdit, FiTrash2, FiCalendar, FiClock, FiMapPin, FiVideo, FiUsers, FiArrowLeft, FiPlus, FiX, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import meetingService from '../../services/meetingService';
import userService from '../../services/userService';

const MeetingView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    user_id: '',
    attendance_status: 'Required',
    response_status: 'No Response',
  });

  useEffect(() => {
    fetchMeeting();
    fetchUsers();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const data = await meetingService.getMeeting(id);
      setMeeting(data.data);
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error('Failed to fetch meeting details');
      navigate('/app/meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getUsers({ limit: 1000 });
      setUsers(data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      try {
        await meetingService.deleteMeeting(id);
        toast.success('Meeting deleted successfully');
        navigate('/app/meetings');
      } catch (error) {
        console.error('Error deleting meeting:', error);
        toast.error(error.response?.data?.error || 'Failed to delete meeting');
      }
    }
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.user_id) {
      toast.error('Please select a user');
      return;
    }

    try {
      await meetingService.addParticipant(id, newParticipant);
      toast.success('Participant added successfully');
      setShowAddParticipant(false);
      setNewParticipant({
        user_id: '',
        attendance_status: 'Required',
        response_status: 'No Response',
      });
      fetchMeeting();
    } catch (error) {
      console.error('Error adding participant:', error);
      toast.error(error.response?.data?.error || 'Failed to add participant');
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    if (window.confirm('Are you sure you want to remove this participant?')) {
      try {
        await meetingService.removeParticipant(id, participantId);
        toast.success('Participant removed successfully');
        fetchMeeting();
      } catch (error) {
        console.error('Error removing participant:', error);
        toast.error(error.response?.data?.error || 'Failed to remove participant');
      }
    }
  };

  const handleUpdateResponse = async (participantId, responseStatus) => {
    try {
      await meetingService.updateParticipantResponse(id, participantId, { response_status: responseStatus });
      toast.success('Response updated successfully');
      fetchMeeting();
    } catch (error) {
      console.error('Error updating response:', error);
      toast.error(error.response?.data?.error || 'Failed to update response');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Scheduled: 'badge-primary',
      'In Progress': 'badge-warning',
      Completed: 'badge-success',
      Cancelled: 'badge-danger',
    };
    return colors[status] || 'badge-default';
  };

  const getMeetingTypeColor = (type) => {
    const colors = {
      Standup: 'badge-purple',
      Planning: 'badge-blue',
      Review: 'badge-success',
      Retrospective: 'badge-orange',
      Other: 'badge-gray',
    };
    return colors[type] || 'badge-default';
  };

  const getResponseColor = (response) => {
    const colors = {
      Accepted: 'badge-success',
      Declined: 'badge-danger',
      Tentative: 'badge-warning',
      'No Response': 'badge-gray',
    };
    return colors[response] || 'badge-default';
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} minutes`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours} hour${hours > 1 ? 's' : ''} ${mins} minutes` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading meeting details...</div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Meeting not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/meetings')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(meeting.status)}`}>
                {meeting.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMeetingTypeColor(meeting.meeting_type)}`}>
                {meeting.meeting_type}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/app/meetings/${id}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiEdit /> Edit
          </Link>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Meeting Details */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Meeting Details</h2>

            {meeting.description && (
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <p className="text-gray-600 mt-1">{meeting.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FiCalendar className="text-gray-400 mt-1" />
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Time</label>
                  <p className="text-gray-600">{formatDateTime(meeting.start_time)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiClock className="text-gray-400 mt-1" />
                <div>
                  <label className="text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-gray-600">{formatDuration(meeting.start_time, meeting.end_time)}</p>
                </div>
              </div>

              {meeting.location && (
                <div className="flex items-start gap-3">
                  <FiMapPin className="text-gray-400 mt-1" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">Location</label>
                    <p className="text-gray-600">{meeting.location}</p>
                  </div>
                </div>
              )}

              {meeting.meeting_url && (
                <div className="flex items-start gap-3">
                  <FiVideo className="text-gray-400 mt-1" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">Virtual Meeting</label>
                    <a
                      href={meeting.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Join Meeting
                    </a>
                  </div>
                </div>
              )}
            </div>

            {meeting.project && (
              <div>
                <label className="text-sm font-medium text-gray-700">Project</label>
                <Link
                  to={`/app/projects/${meeting.project.id}`}
                  className="text-blue-600 hover:underline block mt-1"
                >
                  {meeting.project.name}
                </Link>
              </div>
            )}

            {meeting.organizer && (
              <div>
                <label className="text-sm font-medium text-gray-700">Organizer</label>
                <p className="text-gray-600 mt-1">
                  {meeting.organizer.first_name} {meeting.organizer.last_name} ({meeting.organizer.email})
                </p>
              </div>
            )}
          </div>

          {/* Agenda */}
          {meeting.agenda && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Agenda</h2>
              <div className="text-gray-600 whitespace-pre-wrap">{meeting.agenda}</div>
            </div>
          )}

          {/* Minutes */}
          {meeting.minutes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Meeting Minutes</h2>
              <div className="text-gray-600 whitespace-pre-wrap">{meeting.minutes}</div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Participants */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FiUsers /> Participants ({meeting.meeting_participants?.length || 0})
              </h2>
              <button
                onClick={() => setShowAddParticipant(!showAddParticipant)}
                className="text-blue-600 hover:text-blue-700"
              >
                <FiPlus />
              </button>
            </div>

            {showAddParticipant && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <select
                  value={newParticipant.user_id}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, user_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>

                <select
                  value={newParticipant.attendance_status}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, attendance_status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="Required">Required</option>
                  <option value="Optional">Optional</option>
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={handleAddParticipant}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <FiCheck /> Add
                  </button>
                  <button
                    onClick={() => setShowAddParticipant(false)}
                    className="flex-1 border border-gray-300 px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    <FiX /> Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {meeting.meeting_participants && meeting.meeting_participants.length > 0 ? (
                meeting.meeting_participants.map((participant) => (
                  <div key={participant.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-gray-900">
                          {participant.user?.first_name} {participant.user?.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{participant.user?.email}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveParticipant(participant.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        participant.attendance_status === 'Required' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {participant.attendance_status}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${getResponseColor(participant.response_status)}`}>
                        {participant.response_status}
                      </span>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => handleUpdateResponse(participant.id, 'Accepted')}
                        className="flex-1 text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleUpdateResponse(participant.id, 'Tentative')}
                        className="flex-1 text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                      >
                        Tentative
                      </button>
                      <button
                        onClick={() => handleUpdateResponse(participant.id, 'Declined')}
                        className="flex-1 text-xs px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No participants added yet</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Information</h2>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-gray-500">Created</label>
                <p className="text-gray-900">{new Date(meeting.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="text-gray-500">Last Updated</label>
                <p className="text-gray-900">{new Date(meeting.updated_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingView;


